'use strict';

(function () {
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var LOADING_MS = REDUCED_MOTION ? 150 : 1400;

  var FALLBACK_QUOTES = [
    'Every piece starts as a single idea.',
    'Good work takes a moment to shape.',
    'Handmade in Athens, one detail at a time.',
    'Almost there — good things are worth the wait.'
  ];

  var SIZED_TYPES = ['Ring', 'Necklace / Pendant', 'Bracelet'];
  var BASE_STEP_ORDER = ['piece_type', 'metal', 'gemstone', 'size', 'category', 'details', 'path'];

  // ── Chatty bridge ──
  // message:send alone turned out to be unreliable — the SDK fires its
  // own message:sent event, but the text doesn't actually land in the
  // visible thread (confirmed live: conversation:started fires with a
  // real id, message:sent fires, and the text is still nowhere in the
  // rendered DOM). set conversation:attributes / set user:context are
  // Chatty's documented, purpose-built way to hand an agent (or the AI)
  // structured context, so the wizard's answers go through those too —
  // and escalate explicitly requests a human handoff, which is the
  // actual "call sign to connect to a representative".
  function openChat() {
    if (!window.$chatty || typeof window.$chatty.push !== 'function') return false;
    try {
      window.$chatty.push(['do', 'chat:open']);
      return true;
    } catch (err) {
      console.error('[custom-jewelry] chat open failed:', err);
      return false;
    }
  }

  // Fires `onFire` the first time `event` happens, or after `timeoutMs`
  // if it never does — whichever comes first, exactly once.
  function once(event, timeoutMs, onFire) {
    var fired = false;
    function fire() {
      if (fired) return;
      fired = true;
      onFire();
    }
    if (window.$chatty && typeof window.$chatty.on === 'function') {
      try { window.$chatty.on(event, fire); } catch (err) { /* fall through to timeout */ }
    }
    setTimeout(fire, timeoutMs);
  }

  // Sequences the handoff through actual SDK events rather than guessed
  // fixed delays — pushing set/message/escalate in the same tick as
  // chat:open dropped them about half the time in live testing (the
  // widget's own open/mount needs a moment to settle), and fixed
  // timeouts are inherently a guess. Each step waits for its real event,
  // falling back to a timeout only if that event never arrives.
  function startChattyConversation(summaryText, attributePairs) {
    if (!window.$chatty || typeof window.$chatty.push !== 'function') return;
    once('chat:opened', 1200, function () {
      try {
        window.$chatty.push(['set', 'conversation:attributes', attributePairs]);
        window.$chatty.push(['set', 'user:context', { customJewelryRequest: summaryText }]);
        window.$chatty.push(['do', 'message:send', 'text', summaryText]);
      } catch (err) {
        console.error('[custom-jewelry] chat send failed:', err);
      }
      once('message:sent', 1500, function () {
        try { window.$chatty.push(['do', 'escalate']); } catch (err) {
          console.error('[custom-jewelry] chat escalate failed:', err);
        }
      });
    });
  }

  function initChatCallout() {
    var btn = document.querySelector('[data-open-chat]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (!openChat()) return;
      // Same event-driven handoff as the wizard's chat path — opens
      // straight into "wants a custom piece, connect me to someone"
      // rather than the bot's generic default menu.
      startChattyConversation(
        "Hi — I'd like to talk about a custom piece.",
        [{ name: 'request_type', value: 'custom_jewelry_inquiry' }]
      );
    });
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // ══════════════════════════════════════════════════════════
  //  Generic swipeable/click-through carousel
  //  (piece type / metal / gemstone all use this)
  // ══════════════════════════════════════════════════════════
  function initCarousel(root) {
    var track = root.querySelector('[data-carousel-track]');
    // allCards never changes; `cards` is the currently visible subset (see
    // setFilter) and is what index/dots/centering are all measured against.
    var allCards = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-card]'));
    var cards = allCards.slice();
    var prevBtn = root.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]');
    var dotsWrap = root.parentElement.querySelector('[data-carousel-dots]');
    if (!track || !cards.length) return;

    var index = 0;
    var dots = [];

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.textContent = '';
      cards.forEach(function (card, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cst-carousel__dot';
        dot.setAttribute('aria-label', 'Go to option ' + (i + 1));
        dot.addEventListener('click', function () { goTo(i); });
        dotsWrap.appendChild(dot);
      });
      dots = Array.prototype.slice.call(dotsWrap.children);
    }
    buildDots();

    // A carousel only ever shows one card at a time, so simply swiping
    // or clicking through to a card IS the selection — no separate
    // "confirm" tap needed. Selection updates automatically on every
    // render(); clicking a card just navigates to it if it isn't
    // already current.
    function select(card) {
      cards.forEach(function (c) { c.classList.remove('is-selected'); });
      card.classList.add('is-selected');
      root.dataset.selectedValue = card.dataset.value;

      var panel = root.closest('.cst-wiz__panel');
      var otherField = panel ? panel.querySelector('[data-gemstone-other-field]') : null;
      if (otherField) {
        var isOther = card.hasAttribute('data-carousel-card-other');
        otherField.hidden = !isOther;
        if (!isOther) {
          var input = otherField.querySelector('[data-field]');
          if (input) input.value = '';
        }
      }

      if (typeof window.__cstSync === 'function') window.__cstSync();
    }

    // Cards are sized narrower than the viewport (see .cst-carousel__card
    // flex-basis) so the previous/next card peeks in on either side of
    // the centered current one — this computes the exact pixel offset
    // needed to keep the current card centered regardless of viewport
    // width, rather than a plain -100%*index slide.
    function centerOffset() {
      var viewport = track.parentElement;
      // offsetWidth (layout box) rather than getBoundingClientRect
      // (rendered box) — non-current cards carry a CSS transform:
      // scale(), and getBoundingClientRect reflects that scale, so
      // measuring an off-center card gave a shrunken width and threw
      // the centering off every time the current card wasn't cards[0].
      var cardWidth = cards[index].offsetWidth;
      var gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0') || 0;
      var pad = (viewport.offsetWidth - cardWidth) / 2;
      return pad - index * (cardWidth + gap);
    }

    function render() {
      track.style.transform = 'translateX(' + centerOffset() + 'px)';
      cards.forEach(function (card, i) {
        card.classList.toggle('is-current', i === index);
        card.setAttribute('tabindex', i === index ? '0' : '-1');
      });
      dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === index); });
      select(cards[index]);
    }

    function goTo(i) {
      index = ((i % cards.length) + cards.length) % cards.length;
      render();
    }

    allCards.forEach(function (card) {
      card.addEventListener('click', function () {
        var i = cards.indexOf(card);
        if (i !== -1) goTo(i);
      });
    });

    // ── Filtering ──
    // Lets the wizard narrow a carousel to the options that make sense for an
    // earlier answer — the metal step showing necklaces in each metal once
    // "Necklace / Pendant" was chosen, rather than a tray of rings. Hidden
    // cards leave the visible list entirely, so index, dots and the centering
    // maths all stay correct. A filter that would empty the carousel is
    // ignored, so a mis-typed piece type in the theme editor degrades to
    // "show everything" rather than a blank step.
    root.__cstFilter = function (predicate) {
      var next = allCards.filter(predicate);
      if (!next.length) next = allCards.slice();
      var previous = cards[index];
      cards = next;
      allCards.forEach(function (card) { card.hidden = cards.indexOf(card) === -1; });
      var keep = cards.indexOf(previous);
      index = keep === -1 ? 0 : keep;
      buildDots();
      render();
    };

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); });

    // Touch swipe
    var startX = null;
    track.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; armHint(); }, { passive: true });
    track.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) goTo(dx < 0 ? index + 1 : index - 1);
      startX = null;
    }, { passive: true });

    // ── Swipe hint (mobile only) ──
    // The arrows are hidden on touch (see CSS), so if a visitor sits on
    // a carousel step for 5s without touching it, nudge the current
    // card over and back to suggest "you can swipe this". Fires once
    // per genuine idle period; any interaction cancels/reschedules it,
    // and it keeps quietly re-checking (rather than giving up) in case
    // the panel wasn't visible yet or the viewport wasn't mobile-width
    // at the time it last checked.
    var hintTimer = null;
    function clearHint() {
      if (hintTimer) { clearTimeout(hintTimer); hintTimer = null; }
    }
    function armHint() {
      clearHint();
      hintTimer = setTimeout(maybePlayHint, 5000);
    }
    function maybePlayHint() {
      var panel = root.closest('.cst-wiz__panel');
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (!isMobile || (panel && panel.hidden) || cards.length < 2) {
        armHint();
        return;
      }
      var base = centerOffset();
      track.style.transition = 'transform 0.4s ease';
      track.style.transform = 'translateX(' + (base - 22) + 'px)';
      setTimeout(function () {
        track.style.transform = 'translateX(' + base + 'px)';
        setTimeout(function () { track.style.transition = ''; }, 400);
      }, 400);
    }
    root.addEventListener('click', armHint);
    armHint();

    // Re-run the centering math on demand — needed because a carousel that
    // initializes inside a hidden wizard panel (every step but the first)
    // reads offsetWidth as 0, so its very first render() centers on a
    // bogus width and the current card sits noticeably left-of-center
    // until something happens to trigger a real 'resize' event. The wizard
    // calls this the moment it un-hides a panel, once layout is real.
    root.__cstRecenter = render;

    render();
    window.addEventListener('resize', render);
  }

  function getCarouselValue(key) {
    var el = document.querySelector('[data-carousel="' + key + '"]');
    return el ? (el.dataset.selectedValue || '') : '';
  }

  function flashInvalid(el) {
    if (!el) return;
    el.classList.remove('is-shake');
    void el.offsetWidth;
    el.classList.add('is-shake');
  }

  // ══════════════════════════════════════════════════════════
  //  Style-category tile grid (single-select, optional)
  // ══════════════════════════════════════════════════════════
  function initTiles(root) {
    var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-tile]'));
    // "Something else" reveals a free-text field, same pattern as the
    // gemstone carousel's Other card — the wizard's validate('category')
    // requires it to be filled before Continue works.
    var otherField = root.parentElement.querySelector('[data-category-other-field]');
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        var already = tile.classList.contains('is-selected');
        tiles.forEach(function (t) { t.classList.remove('is-selected'); });
        if (!already) {
          tile.classList.add('is-selected');
          root.dataset.selectedValue = tile.dataset.value;
        } else {
          root.dataset.selectedValue = '';
        }
        if (otherField) {
          var isCustom = !already && tile.hasAttribute('data-tile-custom');
          otherField.hidden = !isCustom;
          if (!isCustom) {
            var input = otherField.querySelector('[data-field]');
            if (input) input.value = '';
          }
        }
        if (typeof window.__cstSync === 'function') window.__cstSync();
      });
    });
  }

  // ══════════════════════════════════════════════════════════
  //  Main wizard controller
  // ══════════════════════════════════════════════════════════
  function initWizard() {
    var root = document.querySelector('[data-wizard]');
    if (!root) return;

    var quotesEl = root.querySelector('[data-wizard-quotes]');
    var quotes = FALLBACK_QUOTES;
    if (quotesEl) {
      try {
        var parsed = JSON.parse(quotesEl.textContent);
        if (Array.isArray(parsed) && parsed.length && parsed[0]) quotes = parsed;
      } catch (err) { /* keep fallback */ }
    }

    root.querySelectorAll('[data-carousel]').forEach(initCarousel);
    root.querySelectorAll('[data-tiles]').forEach(initTiles);

    var stage = root.querySelector('.cst-wiz__stage');
    var loadingEl = root.querySelector('[data-wizard-loading]');
    var loadingQuoteEl = root.querySelector('[data-loading-quote]');
    var progressFill = root.querySelector('[data-progress-fill]');
    var progressLabel = root.querySelector('[data-progress-label]');
    var progressStepName = root.querySelector('[data-progress-step-name]');
    var backBtn = root.querySelector('[data-wizard-back]');
    var nextBtn = root.querySelector('[data-wizard-next]');
    var nextLabel = root.querySelector('[data-wizard-next-label]');
    var errorEl = root.querySelector('[data-wizard-error]');

    var panels = {};
    root.querySelectorAll('.cst-wiz__panel[data-step]').forEach(function (p) {
      panels[p.dataset.step] = p;
    });

    var detailsField = root.querySelector('[data-step="details"] [data-field="details"]');
    var emailField = root.querySelector('[data-step="path"] [data-field="email"]');
    var emailFieldWrap = root.querySelector('[data-email-field]');

    var state = { path: null };
    var currentStepKey = 'piece_type';

    // ── Metal step: only the metals offered for the chosen piece type ──
    // Each "Wizard: metal" block can name the piece types it applies to. A
    // block with a matching piece type wins over a blank-filter block of the
    // same metal name, which is what lets a shop upload one photo of Sterling
    // Silver as a ring and another as a necklace and have each show up only
    // where it belongs; a metal with no piece-type-specific block at all keeps
    // showing its general photo.
    var metalCarousel = root.querySelector('[data-carousel="metal"]');

    function normalizeOption(value) {
      return (value || '').trim().toLowerCase();
    }

    function cardPieceTypes(card) {
      return (card.dataset.pieceTypes || '')
        .split(',')
        .map(normalizeOption)
        .filter(Boolean);
    }

    // Swap each metal's photo to the one shot for this kind of piece. The map
    // comes from the block's photo slots; a metal with no photo for this piece
    // type falls back to its general photo, so a half-filled setup still looks
    // deliberate rather than blank.
    function applyMetalPhotos(pieceType) {
      metalCarousel.querySelectorAll('[data-carousel-card]').forEach(function (card) {
        var img = card.querySelector('.cst-carousel__media img');
        if (!img) return;
        var map;
        try {
          map = JSON.parse(card.dataset.pieceImages || '{}');
        } catch (err) {
          map = {};
        }
        var src = map[pieceType] || card.dataset.defaultImage || '';
        if (!src || img.getAttribute('src') === src) return;
        // image_tag can emit a srcset; leaving it in place would let the
        // browser keep serving the old photo at some viewport widths.
        img.removeAttribute('srcset');
        img.setAttribute('src', src);
      });
    }

    function syncMetalOptions() {
      if (!metalCarousel || typeof metalCarousel.__cstFilter !== 'function') return;
      var pieceType = normalizeOption(getCarouselValue('piece_type'));
      var covered = Object.create(null);
      metalCarousel.querySelectorAll('[data-carousel-card]').forEach(function (card) {
        var types = cardPieceTypes(card);
        if (types.length && types.indexOf(pieceType) !== -1) {
          covered[normalizeOption(card.dataset.metalName)] = true;
        }
      });
      metalCarousel.__cstFilter(function (card) {
        var types = cardPieceTypes(card);
        if (types.length) return types.indexOf(pieceType) !== -1;
        return !covered[normalizeOption(card.dataset.metalName)];
      });
      applyMetalPhotos(pieceType);
    }

    // ── Size step: units ──
    // Ring/necklace/bracelet sizing conventions vary by market — a US
    // visitor thinks in US ring sizes and inches, most everywhere else
    // thinks in EU sizes and cm, the UK has its own ring-size letters.
    // localization.country.iso_code (rendered server-side into
    // data-market) picks the sensible starting point; the dropdown is
    // always free to change, so nobody's stuck with the wrong system.
    var MARKET = (root.dataset.market || '').toUpperCase();
    var IMPERIAL_MARKETS = ['US', 'LR', 'MM']; // the only markets that default to inches
    var CHAIN_LENGTHS = {
      'in': [['16"', '16"'], ['18"', '18"'], ['20"', '20"'], ['22"', '22"'], ['24"', '24"']],
      'cm': [['40cm', '40cm'], ['45cm', '45cm'], ['50cm', '50cm'], ['55cm', '55cm'], ['60cm', '60cm']]
    };

    function defaultRingUnit() {
      if (MARKET === 'US' || MARKET === 'CA') return 'US';
      if (MARKET === 'GB') return 'UK';
      return 'EU';
    }
    function defaultLengthUnit() {
      return IMPERIAL_MARKETS.indexOf(MARKET) !== -1 ? 'in' : 'cm';
    }

    function renderChainLengthOptions(unit) {
      var select = root.querySelector('[data-chain-length-select]');
      if (!select) return;
      var rows = CHAIN_LENGTHS[unit] || CHAIN_LENGTHS['in'];
      var html = '<option value="" disabled selected>Select one</option>';
      rows.forEach(function (row) { html += '<option value="' + row[0] + '">' + row[1] + '</option>'; });
      html += '<option value="Not sure yet">Not sure yet</option>';
      select.innerHTML = html;
    }

    // Each unit <select> gets its market default applied once, the first
    // time the size step is shown — after that it's the visitor's choice
    // and this leaves it alone.
    var unitDefaultsApplied = false;
    function applyUnitDefaults() {
      if (unitDefaultsApplied) return;
      unitDefaultsApplied = true;
      var ringUnit = root.querySelector('#cst-ring-size-unit');
      if (ringUnit) ringUnit.value = defaultRingUnit();
      var lengthUnit = root.querySelector('#cst-chain-length-unit');
      var lengthDefault = defaultLengthUnit();
      if (lengthUnit) lengthUnit.value = lengthDefault;
      renderChainLengthOptions(lengthDefault);
      var braceletUnit = root.querySelector('#cst-bracelet-size-unit');
      if (braceletUnit) braceletUnit.value = lengthDefault;
    }

    var chainLengthUnitSelect = root.querySelector('#cst-chain-length-unit');
    if (chainLengthUnitSelect) {
      chainLengthUnitSelect.addEventListener('change', function () {
        // Values differ between systems (16" vs 40cm aren't the same
        // length), so switching units resets the choice rather than
        // silently keeping a now-mismatched selection.
        renderChainLengthOptions(chainLengthUnitSelect.value);
        if (typeof window.__cstSync === 'function') window.__cstSync();
      });
    }

    // ── Size step: show the right sub-fields for the chosen piece type ──
    function syncSizeGroups() {
      var pt = getCarouselValue('piece_type');
      root.querySelectorAll('[data-size-group]').forEach(function (group) {
        group.hidden = group.dataset.sizeGroup !== pt;
      });
      applyUnitDefaults();
    }

    function getStepOrder() {
      var order = BASE_STEP_ORDER.slice();
      var pt = getCarouselValue('piece_type');
      if (SIZED_TYPES.indexOf(pt) === -1) {
        order = order.filter(function (k) { return k !== 'size'; });
      }
      return order;
    }

    // ── Path choice (step 7) ──
    var pathChoiceEl = root.querySelector('[data-path-choice]');
    if (pathChoiceEl) {
      pathChoiceEl.querySelectorAll('[data-path-card]').forEach(function (card) {
        card.addEventListener('click', function () {
          pathChoiceEl.querySelectorAll('[data-path-card]').forEach(function (c) { c.classList.remove('is-selected'); });
          card.classList.add('is-selected');
          state.path = card.dataset.value;
          if (emailFieldWrap) emailFieldWrap.hidden = state.path !== 'email';
          syncNext();
        });
      });
    }
    if (emailField) emailField.addEventListener('input', syncNext);
    if (detailsField) detailsField.addEventListener('input', syncNext);

    var gemstoneOtherField = root.querySelector('[data-field="gemstone_other"]');
    var categoryOtherField = root.querySelector('[data-field="category_other"]');
    if (gemstoneOtherField) gemstoneOtherField.addEventListener('input', syncNext);
    if (categoryOtherField) categoryOtherField.addEventListener('input', syncNext);

    function validate(stepKey) {
      switch (stepKey) {
        case 'piece_type': return !!getCarouselValue('piece_type');
        case 'metal': return !!getCarouselValue('metal');
        case 'gemstone':
          // "Something else" only counts once the visitor has actually
          // named a stone — otherwise Babis gets a request that just says
          // "Other" with nothing to go on.
          if (getCarouselValue('gemstone') === 'Other') {
            return !!(gemstoneOtherField && gemstoneOtherField.value.trim().length >= 2);
          }
          return true;
        case 'size': return true;
        case 'category':
          // Same rule as gemstone's Other — picking "Something else" commits
          // to actually describing it.
          if (root.querySelector('[data-tiles="category"]').dataset.selectedValue === 'Custom') {
            return !!(categoryOtherField && categoryOtherField.value.trim().length >= 2);
          }
          return true;
        case 'details': return !!(detailsField && detailsField.value.trim().length >= 10);
        case 'path':
          if (state.path === 'chat') return true;
          if (state.path === 'email') return !!(emailField && isValidEmail(emailField.value.trim()));
          return false;
        default: return true;
      }
    }

    function nextLabelFor(stepKey) {
      if (stepKey === 'path') {
        if (state.path === 'email') return 'Send request →';
        if (state.path === 'chat') return 'Start the conversation →';
        return 'Choose an option';
      }
      return 'Continue';
    }

    function syncNext() {
      if (nextLabel) nextLabel.textContent = nextLabelFor(currentStepKey);
      nextBtn.disabled = !validate(currentStepKey);
    }
    window.__cstSync = syncNext;

    function showQuote() {
      var q = quotes[Math.floor(Math.random() * quotes.length)];
      if (loadingQuoteEl) loadingQuoteEl.textContent = q;
    }

    function updateProgress() {
      var order = getStepOrder();
      var idx = order.indexOf(currentStepKey);
      var total = order.length;
      var pct = total ? ((idx + 1) / total) * 100 : 100;
      if (progressFill) progressFill.style.width = pct + '%';
      if (progressLabel) progressLabel.textContent = 'Step ' + (idx + 1) + ' of ' + total;
      if (progressStepName && panels[currentStepKey]) progressStepName.textContent = panels[currentStepKey].dataset.stepName || '';
      if (backBtn) backBtn.hidden = idx <= 0;
    }

    function hideAllPanels() {
      Object.keys(panels).forEach(function (k) { panels[k].hidden = true; });
    }

    // Steps with no reference photos at all (just text/choice cards) — the
    // "these photos are previews" disclaimer doesn't apply there and reads
    // as confusing leftover copy, so it's hidden on those steps only.
    var PHOTO_FREE_STEPS = ['path', 'confirm'];
    var previewNote = root.querySelector('[data-preview-note]');

    function showPanel(key) {
      hideAllPanels();
      currentStepKey = key;
      if (key === 'metal') syncMetalOptions();
      if (key === 'size') syncSizeGroups();
      if (previewNote) previewNote.hidden = PHOTO_FREE_STEPS.indexOf(key) !== -1;
      panels[key].hidden = false;
      panels[key].classList.remove('cst-wiz__panel--enter');
      void panels[key].offsetWidth;
      panels[key].classList.add('cst-wiz__panel--enter');
      // Layout is real now that the panel is visible — recenter any
      // carousel(s) inside it (see __cstRecenter in initCarousel).
      panels[key].querySelectorAll('[data-carousel]').forEach(function (el) {
        if (typeof el.__cstRecenter === 'function') el.__cstRecenter();
      });
      updateProgress();
      syncNext();
      // Deliberately no auto-scroll here — jumping the page on every
      // step change fought the user's own scroll position (forcing a
      // scroll back up just to see the next question).
    }

    function runTransition(nextKey, withLoading, done) {
      if (!withLoading) {
        showPanel(nextKey);
        if (done) done();
        return;
      }
      showQuote();
      loadingEl.hidden = false;
      nextBtn.disabled = true;
      setTimeout(function () {
        loadingEl.hidden = true;
        showPanel(nextKey);
        if (done) done();
      }, LOADING_MS);
    }

    function goNext() {
      if (!validate(currentStepKey)) {
        // The carousel/tile selection itself is often already valid here
        // (e.g. "Something else" IS picked) — what's missing is the text
        // that goes with it, so flash and focus that instead of the
        // already-satisfied carousel.
        var target;
        if (currentStepKey === 'gemstone' && getCarouselValue('gemstone') === 'Other') {
          target = gemstoneOtherField;
        } else if (currentStepKey === 'category' && root.querySelector('[data-tiles="category"]').dataset.selectedValue === 'Custom') {
          target = categoryOtherField;
        } else {
          target = root.querySelector('.cst-wiz__panel:not([hidden]) [data-carousel], .cst-wiz__panel:not([hidden]) [data-path-choice]');
        }
        flashInvalid(target);
        if (target && typeof target.focus === 'function' && (target === gemstoneOtherField || target === categoryOtherField)) target.focus();
        if (currentStepKey === 'path' && errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = state.path ? 'Please enter a valid email address.' : 'Please choose how you’d like to connect.';
        }
        return;
      }
      if (errorEl) errorEl.hidden = true;

      if (currentStepKey === 'path') { finalize(); return; }

      var order = getStepOrder();
      var idx = order.indexOf(currentStepKey);
      var nextKey = order[idx + 1];
      runTransition(nextKey, true);
    }

    function goBack() {
      var order = getStepOrder();
      var idx = order.indexOf(currentStepKey);
      var prevKey = order[idx - 1];
      if (!prevKey) return;
      runTransition(prevKey, false);
    }

    nextBtn.addEventListener('click', goNext);
    if (backBtn) backBtn.addEventListener('click', goBack);

    // Resolves the category tile selection to display text — substitutes
    // the free-text field when "Something else" was chosen, same pattern
    // as the gemstone carousel's Other handling above.
    function resolveCategoryValue() {
      var raw = root.querySelector('[data-tiles="category"]').dataset.selectedValue;
      if (raw === 'Custom') {
        return categoryOtherField && categoryOtherField.value.trim() ? categoryOtherField.value.trim() : 'Something else (unspecified)';
      }
      return raw;
    }

    // Resolves a size group's value for display — appends the chosen
    // unit for ring/bracelet (bare numbers like "9" or "16" need it to
    // be unambiguous); necklace chain-length values already carry their
    // own unit (e.g. 40cm), so nothing is appended there.
    function resolveSizeValue(pt, group) {
      var input = group.querySelector('[data-field]:not([data-unit-select])');
      if (!input || !input.value.trim()) return '';
      var value = input.value.trim();
      if (pt === 'Necklace / Pendant') return value;
      var unitSelect = group.querySelector('[data-unit-select]');
      if (unitSelect && unitSelect.value) return value + ' (' + unitSelect.value + ')';
      return value;
    }

    // ── Summary text shared by both finalize paths ──
    function buildSummary() {
      var pt = getCarouselValue('piece_type');
      var lines = ['New custom piece request:', '', 'Piece type: ' + pt];
      lines.push('Metal: ' + getCarouselValue('metal'));

      var gem = getCarouselValue('gemstone');
      if (gem === 'Other') {
        var other = root.querySelector('[data-field="gemstone_other"]');
        gem = other && other.value.trim() ? other.value.trim() : 'Other (unspecified)';
      }
      if (gem && gem !== 'No gemstone') lines.push('Gemstone: ' + gem);

      if (SIZED_TYPES.indexOf(pt) !== -1) {
        var group = root.querySelector('[data-size-group="' + pt + '"]');
        if (group) {
          var sizeValue = resolveSizeValue(pt, group);
          if (sizeValue) lines.push('Size: ' + sizeValue);
        }
      }

      var category = resolveCategoryValue();
      if (category) lines.push('Style direction: ' + category);

      lines.push('');
      lines.push('Description:');
      lines.push(detailsField ? detailsField.value.trim() : '');

      if (state.path === 'email' && emailField) {
        lines.push('');
        lines.push('Contact email: ' + emailField.value.trim());
      }

      return lines.join('\n');
    }

    // Structured {name, value} pairs for Chatty's "set conversation:attributes"
    // — the reliable, documented way to hand an agent/AI structured facts,
    // rather than depending on message:send text actually rendering.
    function buildAttributePairs() {
      var pt = getCarouselValue('piece_type');
      var pairs = [
        { name: 'piece_type', value: pt },
        { name: 'metal', value: getCarouselValue('metal') }
      ];

      var gem = getCarouselValue('gemstone');
      if (gem === 'Other') {
        var other = root.querySelector('[data-field="gemstone_other"]');
        gem = other && other.value.trim() ? other.value.trim() : 'Other (unspecified)';
      }
      if (gem && gem !== 'No gemstone') pairs.push({ name: 'gemstone', value: gem });

      if (SIZED_TYPES.indexOf(pt) !== -1) {
        var group = root.querySelector('[data-size-group="' + pt + '"]');
        var sizeValue = group ? resolveSizeValue(pt, group) : '';
        if (sizeValue) pairs.push({ name: 'size', value: sizeValue });
      }

      var category = resolveCategoryValue();
      if (category) pairs.push({ name: 'style_direction', value: category });

      if (detailsField && detailsField.value.trim()) {
        pairs.push({ name: 'customer_description', value: detailsField.value.trim() });
      }

      return pairs;
    }

    function showConfirm(kind) {
      var confirmPanel = panels.confirm;
      confirmPanel.querySelectorAll('[data-confirm-chat],[data-confirm-email]').forEach(function (el) { el.hidden = true; });
      var target = confirmPanel.querySelector(kind === 'chat' ? '[data-confirm-chat]' : '[data-confirm-email]');
      if (target) target.hidden = false;
      showPanel('confirm');
      if (progressLabel) progressLabel.textContent = 'Done';
      if (progressStepName) progressStepName.textContent = 'Thank you';
      if (progressFill) progressFill.style.width = '100%';
      var nav = root.querySelector('.cst-wiz__nav');
      if (nav) nav.hidden = true;
    }

    function finalize() {
      if (state.path === 'chat') {
        if (!openChat()) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Chat isn’t available right now — please try again in a moment, or choose email instead.';
          }
          return;
        }
        startChattyConversation(buildSummary(), buildAttributePairs());
        runTransition('confirm', true, function () { showConfirm('chat'); });
        return;
      }

      // This store has spam protection (reCAPTCHA/hCaptcha) enabled for
      // contact forms. Shopify's captcha-bootstrap script only attaches a
      // token to a *real submit event on a real {% form 'contact' %}* — a
      // hand-built FormData posted with fetch() is rejected server-side with
      // "400 Missing CAPTCHA token", which is exactly why this path never
      // delivered. So we fill the hidden Shopify form and let its own submit
      // flow run: requestSubmit() fires the submit event (unlike .submit(),
      // which skips listeners and would drop the token again), Shopify adds
      // the token and posts it. The page then reloads and the confirmation
      // is shown from the posted-successfully marker at init.
      var postForm = document.getElementById('CustomJewelryContactForm');
      var emailInput = postForm && postForm.querySelector('[data-cst-post="email"]');
      var bodyInput = postForm && postForm.querySelector('[data-cst-post="body"]');

      if (!postForm || !emailInput || !bodyInput) {
        console.error('[custom-jewelry] contact form markup missing');
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = 'Something went wrong sending your request — please try again, or use live chat instead.';
        }
        return;
      }

      emailInput.value = emailField ? emailField.value.trim() : '';
      bodyInput.value = buildSummary();

      nextBtn.disabled = true;
      showQuote();
      loadingEl.hidden = false;

      if (typeof postForm.requestSubmit === 'function') {
        postForm.requestSubmit();
      } else {
        // Older browsers: dispatch the event ourselves so Shopify's captcha
        // listener still runs, then fall back to a native submit.
        var evt = new Event('submit', { bubbles: true, cancelable: true });
        var notCancelled = postForm.dispatchEvent(evt);
        if (notCancelled) postForm.submit();
      }
    }

    // Initial state
    hideAllPanels();
    panels.piece_type.hidden = false;
    updateProgress();
    syncNext();

    // Coming back from a successful send: the submit is a real page POST
    // (see finalize), so the wizard restarts from scratch on the reload —
    // jump straight to the confirmation instead of dropping the visitor
    // back on step 1 wondering whether anything happened.
    if (document.querySelector('[data-cst-posted]')) {
      showConfirm('email');
    }
  }

  // ══════════════════════════════════════════════════════════
  //  Ring size guide — centered modal (not the sitewide side drawer)
  // ══════════════════════════════════════════════════════════
  function initSizeGuideModal() {
    var modal = document.querySelector('[data-size-guide-modal]');
    var triggers = Array.prototype.slice.call(document.querySelectorAll('[data-open-size-guide]'));
    if (!modal || !triggers.length) return;

    var contents = {
      ring: modal.querySelector('[data-guide-content="ring"]'),
      necklace: modal.querySelector('[data-guide-content="necklace"]')
    };
    var unitButtons = Array.prototype.slice.call(modal.querySelectorAll('[data-size-modal-unit]'));

    function setActiveUnit(unit) {
      modal.querySelectorAll('[data-unit-value]').forEach(function (el) {
        el.hidden = el.dataset.unitValue !== unit;
      });
      unitButtons.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.sizeModalUnit === unit);
      });
    }
    unitButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { setActiveUnit(btn.dataset.sizeModalUnit); });
    });

    // Same imperial-markets list the size step itself uses for its unit
    // dropdown default — kept local rather than shared across closures so
    // this modal works even if the wizard's own script order ever changes.
    var wizardRoot = document.querySelector('[data-wizard]');
    var market = ((wizardRoot && wizardRoot.dataset.market) || '').toUpperCase();
    var defaultUnit = ['US', 'LR', 'MM'].indexOf(market) !== -1 ? 'in' : 'cm';

    function open(kind) {
      Object.keys(contents).forEach(function (key) {
        if (contents[key]) contents[key].hidden = key !== kind;
      });
      modal.querySelector('.cst-size-modal__panel').setAttribute(
        'aria-labelledby',
        kind === 'necklace' ? 'cst-size-modal-title-necklace' : 'cst-size-modal-title'
      );
      if (kind === 'necklace') setActiveUnit(defaultUnit);
      modal.hidden = false;
      requestAnimationFrame(function () {
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
      });
      document.addEventListener('keyup', onKeyUp);
    }

    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keyup', onKeyUp);
      setTimeout(function () { modal.hidden = true; }, 250);
    }

    function onKeyUp(e) { if (e.key === 'Escape') close(); }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        open(trigger.dataset.sizeGuideKind || 'ring');
      });
    });
    modal.querySelectorAll('[data-size-guide-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
  }

  // ── Captcha binding for the wizard's hidden contact form ──
  // Shopify's captcha-bootstrap binds a protected form when it sees a
  // focusin/change inside it, or — at DOMContentLoaded — when the form
  // carries data-shopify-captcha="true". Our form is hidden and filled
  // programmatically, so it never emits focusin/change and would never get
  // bound; an unbound protected form has its submit event preventDefault-ed
  // and silently swallowed. Setting the attribute here (top level of a
  // deferred script, which runs *before* DOMContentLoaded, and so before
  // the bootstrap's own listener) opts it into that binding pass.
  (function markContactFormForCaptcha() {
    var form = document.getElementById('CustomJewelryContactForm');
    if (form) form.setAttribute('data-shopify-captcha', 'true');
  })();

  document.addEventListener('DOMContentLoaded', function () {
    initChatCallout();
    initWizard();
    initSizeGuideModal();

    // Safety net: if the attribute pass above didn't get the form bound
    // (script order changes, a Shopify bootstrap update, etc.), bind it
    // explicitly through the public API. Guarded on Shopify's own
    // already-bound marker so we never double-bind.
    var form = document.getElementById('CustomJewelryContactForm');
    if (
      form &&
      !form.dataset.cptcha &&
      window.Shopify &&
      window.Shopify.captcha &&
      typeof window.Shopify.captcha.protect === 'function'
    ) {
      try {
        window.Shopify.captcha.protect(form);
      } catch (err) {
        console.error('[custom-jewelry] captcha protect failed:', err);
      }
    }
  });
})();
